/**
 * Reading the applied personalization (SKE-4166, phase 4).
 *
 * Binds React to the demo store the same way `useDemoMirror` does — the brand
 * lives inside the store's `settings`, so a personalization write publishes to
 * the same listeners every other demo mutation does, and every surface that
 * shows the brand re-renders from one source with no second fetch and no
 * duplicated state.
 *
 * Both hooks here return null outside demo mode, so they are safe to call
 * unconditionally from shared components on the real reviewer path.
 */

import { useSyncExternalStore } from 'react'

import type { DemoBrand } from './demoBrand'
import { getDemoBrandSnapshot } from './demoApi'
import { isDemoMode } from './demoMode'
import { subscribeDemoStore } from './demoStore'

/** `getSnapshot`, guarded so the real reviewer path never reads a demo store. */
const readBrand = (): DemoBrand | null =>
  isDemoMode() ? getDemoBrandSnapshot() : null

export const useDemoBrand = (): DemoBrand | null =>
  useSyncExternalStore(subscribeDemoStore, readBrand, () => null)

/**
 * Overlay the applied brand onto the review settings the header renders.
 *
 * Homepage loads `setting` once, into state, on mount — so a personalization
 * applied later cannot reach the header unless something overlays it at render
 * time. That is this. Outside demo mode, and before the visitor personalizes,
 * it hands back the exact same object it was given, so the real reviewer path
 * is not merely unchanged in behaviour but unchanged in identity (no extra
 * render, no new props).
 */
export const useDemoBrandedSetting = <
  T extends { logoUrl: string; companyName: string },
>(
  setting: T
): T => {
  const brand = useDemoBrand()

  if (!brand) {
    return setting
  }

  return { ...setting, logoUrl: brand.logoUrl, companyName: brand.name }
}
